import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <Seo title="Privacy Policy" description="How Bharat Estate collects, uses, and protects your personal information." path="/privacy-policy" />
      <div className="container-luxury max-w-4xl">
        <div className="bg-white p-8 md:p-12 rounded-sm shadow-sm border border-border/50">
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#0F172A] mb-8">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2026</p>

          <div className="prose prose-lg max-w-none text-[#0F172A]">
            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Bharat Estate ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using our platform, you agree to this Privacy Policy. If you do not agree with the terms of this policy, please do not access the site.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">2. Information We Collect</h2>
              
              <h3 className="font-semibold text-lg mb-3">2.1 Personal Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">We may collect personal information that you voluntarily provide, including:</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Full name and contact information (email address, phone number)</li>
                <li>Account credentials (username and password)</li>
                <li>Profile information (profile picture, bio)</li>
                <li>Payment and billing information</li>
                <li>Property listing details</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="font-semibold text-lg mb-3">2.2 Automatically Collected Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">When you access our platform, we automatically collect:</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Device information (browser type, operating system)</li>
                <li>IP address and location data</li>
                <li>Usage data (pages visited, time spent, clicks)</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We use the collected information for:</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Creating and managing your account</li>
                <li>Processing property listings and inquiries</li>
                <li>Facilitating communication between buyers and sellers</li>
                <li>Processing premium membership subscriptions</li>
                <li>Sending transactional emails and notifications</li>
                <li>Improving our services and user experience</li>
                <li>Preventing fraud and ensuring security</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">4. Information Sharing</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We may share your information with:</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li><strong>Other Users:</strong> Property listing details are shared publicly. Contact information may be shared with interested buyers/renters.</li>
                <li><strong>Service Providers:</strong> Third-party vendors who assist in operating our platform (payment processors, hosting services, analytics).</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety.</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                We do not sell your personal information to third parties for marketing purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">5. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal information, including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Regular security assessments</li>
                <li>Access controls and monitoring</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">6. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing of your data</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                To exercise these rights, please contact us at contact@bharat.estate
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">7. Cookies Policy</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use cookies and similar technologies to enhance your experience. Types of cookies we use:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for basic site functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use our site</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                You can control cookies through your browser settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">8. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide services. We may retain certain information for legal, business, or regulatory requirements even after account closure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">9. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-semibold mb-4">10. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-[#F9F9F7] p-6 rounded-sm">
                <p className="text-[#0F172A] font-medium">Bharat Estate</p>
                <p className="text-muted-foreground">Email: contact@bharat.estate</p>
                <p className="text-muted-foreground">Phone: +91 94650 24516</p>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t flex gap-4">
            <Link to="/terms" className="text-orange-500 hover:underline">Terms of Service</Link>
            <Link to="/refund-policy" className="text-orange-500 hover:underline">Refund Policy</Link>
            <Link to="/grievance" className="text-orange-500 hover:underline">Grievance Redressal</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
